/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { useBTSChatContext } from './BTSChatContext';
import { useChat } from '@jaybel/chat-lib';
import { IoSend } from 'react-icons/io5';
import { FiCopy, FiExternalLink, FiCheck, FiLink, FiCode, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const URL_OR_LONG_TOKEN_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+|[^\s=]+=[\S]{15,}|[^\s]{28,})/gi;

const isWebUrl = (str) => /^https?:\/\//i.test(str) || /^www\./i.test(str);

// Helper to truncate long display URLs neatly
const formatDisplayUrl = (url, maxLength = 32) => {
  try {
    const fullUrl = url.startsWith('www.') ? `https://${url}` : url;
    const parsed = new URL(fullUrl);
    const domain = parsed.hostname.replace(/^www\./, '');
    const path = parsed.pathname;
    
    let display = `${domain}${path}`;
    if (parsed.search && display.length < maxLength) {
      display += parsed.search;
    }
    
    if (display.length <= maxLength) return display;
    
    const domainAndStartPath = `${domain}${path}`;
    if (domainAndStartPath.length <= maxLength - 3) {
      return `${domainAndStartPath}...`;
    }
    return `${display.slice(0, maxLength - 3)}...`;
  } catch {
    if (url.length <= maxLength) return url;
    return `${url.slice(0, maxLength - 3)}...`;
  }
};

// Component for rendering URLs, query strings, and long unbroken tokens neatly on 1 line with Copy/Open options
const SmartTokenChip = ({ token, chipKey, handleCopyText, copiedId }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  let cleanToken = token;
  let trailingPunctuation = '';
  const trailingMatch = token.match(/([.,!?;:]+)$/);
  if (trailingMatch && !token.includes('=')) {
    trailingPunctuation = trailingMatch[0];
    cleanToken = token.slice(0, -trailingPunctuation.length);
  }

  const isUrl = isWebUrl(cleanToken);
  const href = cleanToken.startsWith('www.') ? `https://${cleanToken}` : cleanToken;
  const isCopied = copiedId === chipKey;

  const shortLabel = isUrl
    ? formatDisplayUrl(cleanToken, 30)
    : cleanToken.length > 30
    ? `${cleanToken.slice(0, 27)}...`
    : cleanToken;

  return (
    <span className="inline-block my-1 max-w-full align-middle">
      <span className="inline-flex items-center gap-2 max-w-full px-2.5 py-1 rounded-lg bg-blue-50/95 hover:bg-blue-100/95 border border-blue-200 text-blue-800 transition-all text-xs font-mono shadow-2xs">
        {isUrl ? (
          <FiLink className="w-3.5 h-3.5 flex-shrink-0 text-blue-600" />
        ) : (
          <FiCode className="w-3.5 h-3.5 flex-shrink-0 text-blue-600" />
        )}

        {/* Truncated text display or expanded view */}
        <span
          className={`select-all ${
            isExpanded
              ? 'whitespace-normal break-all font-sans text-xs text-gray-800 bg-white/80 p-1.5 rounded border border-blue-100'
              : 'truncate max-w-[160px] sm:max-w-[240px] md:max-w-[320px]'
          }`}
          title={cleanToken}
        >
          {isExpanded ? cleanToken : shortLabel}
        </span>

        {/* Action buttons */}
        <span className="flex items-center gap-1 flex-shrink-0 font-sans">
          {/* Open link button if it's a URL */}
          {isUrl && (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title="Open link in new tab"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-blue-700 bg-white hover:bg-blue-50 border border-blue-300 rounded transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <FiExternalLink className="w-3 h-3" />
              <span>Open</span>
            </a>
          )}

          {/* Copy button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleCopyText(cleanToken, chipKey);
            }}
            title="Copy to clipboard"
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded transition-colors"
          >
            {isCopied ? (
              <>
                <FiCheck className="w-3 h-3 text-green-600" />
                <span className="text-green-600 font-semibold">Copied</span>
              </>
            ) : (
              <>
                <FiCopy className="w-3 h-3 text-gray-500" />
                <span>Copy</span>
              </>
            )}
          </button>

          {/* Toggle Expand/Collapse button for long tokens */}
          {cleanToken.length > 30 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              title={isExpanded ? 'Collapse link' : 'Expand link'}
              className="p-1 text-gray-500 hover:text-gray-800 hover:bg-blue-200/50 rounded transition-colors ml-0.5"
            >
              {isExpanded ? (
                <FiChevronUp className="w-3.5 h-3.5" />
              ) : (
                <FiChevronDown className="w-3.5 h-3.5" />
              )}
            </button>
          )}
        </span>
      </span>
      {trailingPunctuation}
    </span>
  );
};

const ChatPanel = () => {
  const { selectedClass, refreshList, updateLastMessageLocally } = useBTSChatContext();
  const [text, setText] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const messagesEndRef = useRef(null);

  // Call the hook only if selectedClass is active
  const chatProps = useChat({
    quoteCode: selectedClass ? String(selectedClass.schoolCode) : '',
    classId: selectedClass ? String(selectedClass.classId) : '',
  });

  const {
    messages = [],
    isLoadingHistory,
    isSending,
    sendMessage,
    currentUser,
    isConnected,
  } = selectedClass ? chatProps : {};

  // Copy text helper with 2s state feedback
  const handleCopyText = (textToCopy, id) => {
    if (!textToCopy) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(textToCopy).catch((err) => console.error('Copy failed', err));
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
      } catch (e) {
        console.error('Copy fallback failed', e);
      }
      document.body.removeChild(textarea);
    }

    setCopiedId(id);
    setTimeout(() => {
      setCopiedId(null);
    }, 2000);
  };

  // Helper to parse message text, convert URLs and long unbroken parameter strings into smart chips
  const renderTextWithLinks = (content, msgId) => {
    if (!content) return null;

    const lines = content.split('\n');

    return lines.map((line, lineIdx) => {
      const matches = Array.from(line.matchAll(URL_OR_LONG_TOKEN_REGEX));
      if (matches.length === 0) {
        return (
          <React.Fragment key={lineIdx}>
            {line}
            {lineIdx < lines.length - 1 && <br />}
          </React.Fragment>
        );
      }

      const lineElements = [];
      let lastIndex = 0;

      matches.forEach((match, matchIdx) => {
        const rawToken = match[0];
        const matchIndex = match.index;

        if (matchIndex > lastIndex) {
          lineElements.push(line.substring(lastIndex, matchIndex));
        }

        const chipKey = `chip-${msgId}-${lineIdx}-${matchIdx}`;

        lineElements.push(
          <SmartTokenChip
            key={chipKey}
            token={rawToken}
            chipKey={chipKey}
            handleCopyText={handleCopyText}
            copiedId={copiedId}
          />
        );

        lastIndex = matchIndex + rawToken.length;
      });

      if (lastIndex < line.length) {
        lineElements.push(line.substring(lastIndex));
      }

      return (
        <React.Fragment key={lineIdx}>
          {lineElements}
          {lineIdx < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  // Scroll to bottom helper
  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Scroll when messages change
  useEffect(() => {
    if (selectedClass && messages.length > 0) {
      scrollToBottom('auto');
    }
  }, [messages, selectedClass]);

  const lastClassIdRef = useRef(null);
  const lastMessageRef = useRef(null);

  // Refresh left panel unread status when room is selected or new incoming messages arrive
  useEffect(() => {
    if (selectedClass) {
      // 1. Refresh list when switching to a different room
      if (selectedClass.classId !== lastClassIdRef.current) {
        refreshList();
        lastClassIdRef.current = selectedClass.classId;
      }

      // 2. Refresh list when a new incoming message is received
      if (messages.length > 0) {
        const lastMsg = messages[messages.length - 1];
        const isMine =
          lastMsg.senderRole === 'REP' ||
          lastMsg.senderRole === (currentUser?.role || 'REP') ||
          lastMsg.senderId === currentUser?.id;

        if (!isMine && lastMsg.id !== lastMessageRef.current?.id) {
          refreshList();
        }
        lastMessageRef.current = lastMsg;
      }
    } else {
      lastClassIdRef.current = null;
    }
  }, [selectedClass, messages, currentUser, refreshList]);

  if (!selectedClass) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 text-gray-500 p-8 h-full min-w-0">
        <svg
          className="w-16 h-16 text-gray-300 mb-4 animate-pulse"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <h3 className="text-lg font-bold text-gray-700 mb-1">Select a Class</h3>
        <p className="text-sm text-gray-400 text-center max-w-xs">
          Choose a school class from the left list to view unread messages and start typing.
        </p>
      </div>
    );
  }

  const handleSend = async () => {
    if (!text.trim() || isSending) return;
    const msgText = text;
    setText('');
    try {
      await sendMessage(msgText);
      updateLastMessageLocally(selectedClass.schoolCode, selectedClass.classId, msgText);
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Helper to format date labels (e.g. Today, Yesterday, Date)
  const getMessageDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' });
    }
  };

  // Helper to format time
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Group messages by date to render dividers
  const renderMessageList = () => {
    const listElements = [];
    let lastDateLabel = null;

    messages.forEach((msg, idx) => {
      if (msg.senderId === 'SYSTEM' && msg.content === 'PING') return;

      const currentDateLabel = getMessageDateLabel(msg.createdAt);
      if (currentDateLabel !== lastDateLabel) {
        listElements.push(
          <div key={`date-${currentDateLabel}-${idx}`} className="flex items-center gap-3 my-6 min-w-0">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider whitespace-nowrap">
              {currentDateLabel}
            </span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>
        );
        lastDateLabel = currentDateLabel;
      }

      // Check if message is mine (outgoing)
      const isMine =
        msg.senderRole === 'REP' ||
        msg.senderRole === (currentUser?.role || 'REP') ||
        msg.senderId === currentUser?.id;

      const msgKey = msg.id || idx;

      listElements.push(
        <div key={msgKey} className={`group/msg flex flex-col mb-4 w-full min-w-0 ${isMine ? 'items-end' : 'items-start'}`}>
          {/* User Name label above incoming bubble */}
          {!isMine && (
            <span className="text-xs font-semibold text-gray-500 mb-1 ml-1 truncate max-w-full">
              User Name
            </span>
          )}

          <div
            className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word] ${
              isMine
                ? 'bg-gray-100 text-gray-800 rounded-tr-none border border-gray-200'
                : 'bg-[#E0F2FE] text-gray-800 rounded-tl-none border border-[#BAE6FD]'
            }`}
          >
            {renderTextWithLinks(msg.content, msgKey)}
          </div>

          {/* Info row below bubble */}
          <div className="flex items-center gap-1.5 mt-1 mx-1 max-w-full">
            <span className="text-[10px] text-gray-400 truncate">
              {isMine ? (
                `${currentUser?.name || 'Sales Rep'} · ${formatTime(msg.createdAt)}`
              ) : (
                `${msg.senderId} · ${formatTime(msg.createdAt)}`
              )}
            </span>

            {/* Copy message text button */}
            <button
              type="button"
              onClick={() => handleCopyText(msg.content, `msg-${msgKey}`)}
              title="Copy message text"
              className="opacity-0 group-hover/msg:opacity-100 focus:opacity-100 transition-opacity p-0.5 text-gray-400 hover:text-gray-600 rounded ml-1"
            >
              {copiedId === `msg-${msgKey}` ? (
                <span className="flex items-center gap-0.5 text-[10px] text-green-600 font-semibold">
                  <FiCheck className="w-3 h-3 text-green-600" /> Copied
                </span>
              ) : (
                <FiCopy className="w-3 h-3" />
              )}
            </button>
          </div>
        </div>
      );
    });

    return listElements;
  };

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 w-full bg-white">
      {/* Header */}
      <div className="px-6 py-3 bg-[#E5E7EB] border-b border-gray-200 flex items-center justify-between min-h-[64px] min-w-0">
        <div className="flex flex-col justify-center min-w-0 pr-2">
          <h2 className="text-sm font-bold text-gray-800 leading-tight truncate">
            {selectedClass.className}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {selectedClass.schoolName}
          </p>
        </div>

        {/* Connection status indicator */}
        <div className="flex items-center gap-1.5 px-3 py-1 bg-white/60 backdrop-blur-sm rounded-full border border-gray-200 shadow-sm flex-shrink-0">
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 transition-all duration-300 ${
              isConnected ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.8)]' : 'bg-gray-400'
            }`}
          />
          <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-600">
            {isConnected ? 'Online' : 'Connecting...'}
          </span>
        </div>
      </div>

      {/* Message Area */}
      <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0 min-w-0 w-full">
        {isLoadingHistory ? (
          <div className="flex items-center justify-center h-full min-w-0">
            <div className="w-6 h-6 border-2 border-[#E8A020] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 min-w-0">
            <span className="text-sm font-medium">No messages yet. Say hello! 👋</span>
          </div>
        ) : (
          <div className="w-full min-w-0">
            {renderMessageList()}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Row */}
      <div className="p-4 border-t border-gray-100 bg-white min-w-0">
        <div className="flex items-end gap-3 min-w-0">
          <textarea
            placeholder="Type a message..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            rows={Math.min(8, Math.max(2, text.split('\n').length))}
            className="flex-1 min-w-0 px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#E8A020] focus:border-[#E8A020] placeholder-gray-400 resize-none overflow-y-auto"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || isSending}
            className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[rgb(237,28,36)] rounded-full hover:bg-[rgb(217,26,34)] text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[rgb(237,28,36)] mb-0.5"
          >
            <IoSend size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
